/* GCC country rules: currency, social insurance base, end of service, annual leave and ID / IBAN / CR / VAT formats.
 * Catalog: contracts/countries.json. Mirrors api/internal/domain/country.go — both are checked against
 * contracts/test-vectors/domain.json. Saudi Arabia ("SA") is the default everywhere. */
import CATALOG from "../../../contracts/countries.json";
import { COUNTRY_CODES, isCountryCode, type CountryCode, type Employee, type GosiSettings } from "../data/types";

type Bi = [string, string];
/** [upToYears (0 = no limit), monthsOfWagePerYear] */
export type Band = [number, number];
/** Which pay counts: basic+housing (bh), basic+housing+other (bho), basic+housing+transport (bht), basic, or the whole monthly wage (gross). */
export type WageBasis = "bh" | "bho" | "bht" | "basic" | "gross";

export interface CountryInfo {
  code: CountryCode;
  /** URL prefix of the country's marketing pages ("" for Saudi Arabia, the root). */
  slug: string;
  flag: string;
  name: Bi; short: Bi; demonym: Bi;
  currency: { code: string; word: Bi; symbol: Bi; decimals: number; sarRate: number; step: number };
  phone: string; timezone: string; utcOffset: number;
  /** [lookups.BR key, English, Arabic] (first = default). */
  cities: [string, string, string][];
  banks: [string, string][];
  weekend: number[];
  /** Overtime pay as a multiple of the hourly basic wage (day rate). */
  overtime: number;
  vat: { rate: number; label: Bi; pattern: string; hint: Bi; placeholder: string; authority: Bi };
  cr: { label: Bi; pattern: string; hint: Bi; placeholder: string };
  nationalId: { citizen: Bi; resident: Bi; pattern: string; hint: Bi };
  iban: { pattern: string; length: number; example: string };
  si: {
    name: Bi; short: Bi; url: string; base: WageBasis;
    defaults: GosiSettings;
    parts: { ann: Bi; saned: Bi; oh: Bi };
    summary: Bi;
    /** Bahrain: monthly end-of-service contribution for non-citizens, paid to SIO from `from`. */
    expatEosbFund?: { from: string; rates: [number, number][] };
  };
  eosb: {
    wage: WageBasis; minYears: number; bands: Band[]; capMonths: number; resign: [number, number][]; citizens: boolean;
    /** Oman: service before `until` uses the old scale. */
    legacy?: { until: string; bands: Band[] };
    /** Bahrain: the employer's lump sum covers service up to this date (later service is paid by SIO). */
    serviceUntil?: string;
    summary: Bi;
  };
  leave: { annual: [number, number] };
  wps: { name: Bi; file: Bi; url: string };
  ministry: { name: Bi; short: Bi; url: string };
  permits: { name: Bi; url: string };
  localisation: { name: Bi; short: Bi; url: string; summary: Bi };
  heatBan: { from: string; to: string; start: string; end: string };
  laborLaw: Bi;
  hosting: Bi;
}

export const COUNTRIES: CountryInfo[] = (CATALOG as unknown as { countries: CountryInfo[] }).countries;
export const DEFAULT_COUNTRY: CountryCode = "SA";
export { COUNTRY_CODES, isCountryCode };

const BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));
/** The country's catalog entry; unknown or missing codes fall back to Saudi Arabia. */
export function countryOf(code?: string | null): CountryInfo {
  return BY_CODE.get((code || "").toUpperCase() as CountryCode) ?? BY_CODE.get(DEFAULT_COUNTRY)!;
}
/** Marketing URL slug ("ae") → country, or null. "" / "sa" is Saudi Arabia. */
export function countryBySlug(slug?: string | null): CountryInfo | null {
  const s = (slug || "").toLowerCase();
  if (s === "" || s === "sa") return countryOf("SA");
  return COUNTRIES.find((c) => c.slug === s) ?? null;
}
/** Country code from a query-string value ("ae", "AE", "uae" → "AE"); null when unknown. */
export function parseCountry(v?: string | null): CountryCode | null {
  const s = (v || "").trim().toUpperCase();
  if (s === "UAE") return "AE";
  return isCountryCode(s) ? s : null;
}

/** Social-insurance defaults of a country (copied into the tenant's settings when it signs up or changes country). */
export function siDefaults(country?: string | null): GosiSettings {
  return { ...countryOf(country).si.defaults };
}

/** Pay that counts for a wage basis. */
export function wageBasis(e: Pick<Employee, "basic" | "housing" | "transport" | "other">, basis: WageBasis): number {
  switch (basis) {
    case "basic": return e.basic;
    case "bh": return e.basic + e.housing;
    case "bho": return e.basic + e.housing + e.other;
    case "bht": return e.basic + e.housing + e.transport;
    default: return e.basic + e.housing + e.transport + e.other;
  }
}

/** Months of wage per year of service for `years` over a band ladder. Returns [first band amount, total]. */
export function bandMonths(years: number, bands: Band[]): [number, number] {
  let prev = 0, total = 0, first = 0;
  bands.forEach(([upTo, m], i) => {
    const top = upTo > 0 ? Math.min(years, upTo) : years;
    const span = Math.max(0, top - prev);
    total += span * m;
    if (i === 0) first = span * m;
    if (upTo > 0) prev = upTo;
  });
  return [first, total];
}

/** Factor of a ladder [[belowYears, factor], …, [0, factor]] for `years` (empty ladder = 1). */
export function ladder(years: number, steps: [number, number][]): number {
  for (const [below, f] of steps) if (below === 0 || years < below) return f;
  return 1;
}

/** City keys (lookups.BR) a company in this country picks from: its own cities; Saudi Arabia keeps every other key. */
export function cityKeys(country: string | null | undefined, all: string[]): string[] {
  const c = countryOf(country);
  if (c.code !== "SA") return c.cities.map((x) => x[0]);
  const other = new Set(COUNTRIES.filter((x) => x.code !== "SA").flatMap((x) => x.cities.map((y) => y[0])));
  return all.filter((k) => !other.has(k));
}

/* ---------- formats ---------- */
const norm = (s: string) => String(s || "").replace(/\s+/g, "").toUpperCase();
export function validIbanFor(s: string, country?: string | null): boolean {
  return new RegExp(countryOf(country).iban.pattern).test(norm(s));
}
export function validCrFor(s: string, country?: string | null): boolean {
  return new RegExp(countryOf(country).cr.pattern).test(String(s || "").replace(/\s/g, ""));
}
/** VAT / tax number: optional; countries without VAT accept only an empty value. */
export function validVatFor(s: string, country?: string | null): boolean {
  const v = String(s || "").replace(/\s/g, "");
  if (!v) return true;
  const p = countryOf(country).vat.pattern;
  return !!p && new RegExp(p).test(v.toUpperCase());
}
/** National ID / residence number. Saudi Arabia: 10 digits, 1 = Saudi, 2 = resident; elsewhere one format for everyone. */
export function validIdFor(id: string, nationality: string, country?: string | null): boolean {
  const c = countryOf(country);
  const s = String(id || "").trim();
  if (c.code === "SA") return /^\d{10}$/.test(s) && s[0] === (nationality === "SA" ? "1" : "2");
  return new RegExp(c.nationalId.pattern).test(s);
}
/** Validation messages (same text as the Go API). */
export function idMessage(nationality: string, country?: string | null): string {
  const c = countryOf(country);
  if (c.code === "SA") return nationality === "SA" ? "National ID must be 10 digits starting with 1" : "Iqama number must be 10 digits starting with 2";
  return `${c.nationalId.resident[0]} must be ${c.nationalId.hint[0]}`;
}
export function ibanMessage(country?: string | null): string {
  const c = countryOf(country);
  if (c.code === "SA") return "IBAN must be SA followed by 22 digits";
  return `IBAN must be a ${c.code} IBAN of ${c.iban.length} characters`;
}

/* ---------- money ---------- */
/** Convert a SAR amount to a country's currency, rounded to a "nice" step (demo data and marketing examples). */
export function fromSar(amount: number, country?: string | null, step?: number): number {
  const c = countryOf(country);
  if (!amount) return 0;
  const s = step ?? c.currency.step;
  const v = amount * c.currency.sarRate;
  return Math.max(s, Math.round(v / s) * s);
}
